// @ts-nocheck
import i18n from 'i18next';
// eslint-disable-next-line @spaced-out/i18n/no-react-i18next-import
import {initReactI18next} from 'react-i18next';
import Backend from 'i18next-chained-backend';
import LocalStorageBackend from 'i18next-localstorage-backend';
import HttpApi from 'i18next-http-backend';
import {isMissingTranslationsLoggingEnabled} from 'src/utils';


const localUntranslatedSet = new Set();

// in-memory fallback for language storage
let languageFallback = 'en';

// cache options for local storage backend
const cacheOptions = {
  enabled: true,
  prefix: 'sense-label-i18next-',
  expirationTime: 30 * 24 * 60 * 60 * 1000, // 30 days
  defaultVersion: 'v1.1',
  versions: {en: 'v1.1'},
  store: typeof window !== 'undefined' ? window.localStorage : null,
} as const;

// Helper function to schedule work during idle time
const scheduleIdleWork = (callback: unknown) => {
  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    // Use requestIdleCallback if available
    window.requestIdleCallback(callback, {timeout: 5000});
  } else {
    // Fallback to setTimeout for browsers without requestIdleCallback
    setTimeout(callback, 0);
  }
};

// function to construct API load path
const getLoadPath = (lng: unknown, ns: unknown) =>
  '/api/v1/configurations/label?lng=' + lng + '&ns=' + ns;

const httpApiOptions = {
  loadPath: getLoadPath('en', 'translations'),
  parse(data: unknown) {
    return JSON.parse(data);
  },
  requestOptions: {
    mode: 'cors',
    credentials: 'include',
    cache: 'default',
  },
} as const;

// dynamically read available languages from local JSON files
const getAvailableLanguages = () => {
  try {
    const context = require.context('./translations', false, /\.json$/);

    const languages = new Set(
      context.keys().map((file) => {
        const match = file.match(/([^/]+)\.json$/);
        return match ? match[1] : null;
      }),
    );

    return Array.from(languages).filter(Boolean);
  } catch (error: any) {
    console.error('Error fetching available languages:', error);
    return [];
  }
};

// load translations for a specific language
const loadTranslations = async (language: unknown) => {
  try {
    const translationData = await import(`./translations/${language}.json`);

    let translations;
    if (
      typeof translationData === 'string' ||
      typeof translationData?.default === 'string'
    ) {
      // Case 1: Webpack resolved to a URL, fetch the JSON
      const response = await fetch(translationData.default || translationData);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch translation for ${language}: ${response.statusText}`,
        );
      }
      translations = await response.json();
    } else {
      // Case 2: Webpack provided a JSON object directly
      translations = translationData.default || translationData;
    }

    return translations;
  } catch (error: any) {
    console.error(`Error loading translation for ${language}:`, error);
    return null;
  }
};

// Create i18n instance
const labelI18nInstance = i18n.createInstance();

// Initialize i18n instance
let i18nInitialized = false;

const sourceMapConsumers = new Map();

async function getSourceMapConsumer(bundleUrl: string) {
  if (sourceMapConsumers.has(bundleUrl)) {
    return sourceMapConsumers.get(bundleUrl);
  }

  try {
    const {SourceMapConsumer} = await import('source-map');

    const sourceMapUrl = `${bundleUrl}.map`;

    const resp = await fetch(sourceMapUrl);
    if (!resp.ok) {
      throw new Error(`Failed to fetch source map: ${resp.statusText}`);
    }

    const rawSourceMap = await resp.json();
    const consumer = await new SourceMapConsumer(rawSourceMap);

    sourceMapConsumers.set(bundleUrl, consumer);

    return consumer;
  } catch (err) {
    console.warn(`Could not load source map for ${bundleUrl}:`, err);
    sourceMapConsumers.set(bundleUrl, null);
    return null;
  }
}

function extractBundlePositionFromStackLine(stackLine) {
  if (!stackLine || typeof stackLine !== 'string') {
    return null;
  }

  let component = null;
  let pos = null;

  const namedMatch = stackLine.match(
    /at\s+([\w.$<>]+)\s+\(([^)]+):(\d+):(\d+)\)/,
  );
  if (namedMatch) {
    component = namedMatch[1];
    pos = {
      bundle: namedMatch[2],
      line: Number(namedMatch[3]),
      column: Number(namedMatch[4]),
    };
  } else {
    const anonMatch = stackLine.match(/at\s+([^\s()]+):(\d+):(\d+)/);
    if (anonMatch) {
      pos = {
        bundle: anonMatch[1],
        line: Number(anonMatch[2]),
        column: Number(anonMatch[3]),
      };
    } else {
      const fallbackMatch = stackLine.match(/([^\s()]+):(\d+):(\d+)/);
      if (fallbackMatch) {
        pos = {
          bundle: fallbackMatch[1],
          line: Number(fallbackMatch[2]),
          column: Number(fallbackMatch[3]),
        };
      }
    }
  }

  if (pos && !pos.bundle.includes('new-i18n.ts')) {
    return {component, pos};
  }

  return null;
}

async function mapStackLineToOriginalSource(stackLine) {
  try {
    const extracted = extractBundlePositionFromStackLine(stackLine);

    if (!extracted) {
      return null;
    }

    const {component, pos} = extracted;

    if (pos) {
      const consumer = await getSourceMapConsumer(pos.bundle);

      if (!consumer) {
        return {
          source: 'unknown',
          line: 0,
          column: 0,
          componentName: component || 'unknown',
        };
      }

      const original = consumer.originalPositionFor({
        line: pos.line,
        column: pos.column,
      });

      if (original && original.source) {
        const cleaned = original.source
          .replace(/^webpack:\/\/\/?/, '')
          .replace(/^\.\//, '')
          .replace(/^sense-ui-server\//, '');

        return {
          source: cleaned,
          line: original.line || 0,
          column: original.column || 0,
          componentName: original.name || component || 'unknown',
        };
      }
    }

    if (component) {
      return {
        source: 'unknown',
        line: 0,
        column: 0,
        componentName: component,
      };
    }

    return null;
  } catch (error) {
    console.log('Error in mapStackLineToOriginalSource:', error);
    return null;
  }
}

async function mapStackToBestOriginal(stack) {
  if (!stack) {
    return null;
  }
  const lines = stack
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const mappedSources = [];

  for (let i = 2; i < lines.length; i++) {
    const mapped = await mapStackLineToOriginalSource(lines[i]);
    if (mapped) {
      mappedSources.push(mapped);
    }
  }

  if (mappedSources.length === 0) {
    return null;
  }

  let chosen = null;
  for (let i = mappedSources.length - 1; i >= 0; i--) {
    if (
      /(^|\/)src\//.test(mappedSources[i].source) &&
      !/node_modules/.test(mappedSources[i].source)
    ) {
      chosen = mappedSources[i];
      break;
    }
  }
  if (!chosen) {
    chosen =
      mappedSources.find((c) => !/node_modules/.test(c.source)) ||
      mappedSources[mappedSources.length - 1];
  }

  return chosen;
}

export const initializeTranslations = async (
  agencyDefaultLang: unknown,
  enableMissingTranslationsLogs: unknown,
) => {
  if (i18nInitialized) {
    return;
  }

  const initialLanguage = (() => {
    // manually selected language (from localStorage)
    if (typeof localStorage !== 'undefined') {
      const storedLanguage = localStorage.getItem('selectedLanguage');
      if (storedLanguage) {
        return storedLanguage;
      }
    }
    return agencyDefaultLang || languageFallback;
  })();

  await labelI18nInstance
    .use(Backend)
    .use({
      type: 'languageDetector',
      async: false,
      init: () => {},
      detect: () => initialLanguage,
      cacheUserLanguage: (lng) => {
        // don't cache automatically - only cache when user manually selects
        languageFallback = lng;
      },
    })
    .use(initReactI18next)
    .init({
      debug: false,
      fallbackLng: 'en',
      backend: {
        backends: [
          LocalStorageBackend, // Local cache backend
          HttpApi, // API backend
        ],
        backendOptions: [cacheOptions, httpApiOptions],
      },
      resources: {},
      defaultNS: 'translation',
      ns: ['translation'],
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: false,
        wait: false,
      },
      ...(enableMissingTranslationsLogs
        ? {
            saveMissing: true,
            missingKeyHandler(
              lng: unknown,
              ns: unknown,
              key: unknown,
              fallbackValue: unknown,
            ) {
              try {
                const stack = new Error().stack;
                logUntranslated(key, fallbackValue, stack);
              } catch (err: any) {
                console.error('missingKeyHandler error', err);
              }
            },
          }
        : {}),
    });

  i18nInitialized = true;
  // load the languages after i18n is initialized, & first load the default language
  const defaultLanguage = agencyDefaultLang || 'en';
  // default language is loaded first
  loadLanguageAndTranslationResourceBundle(defaultLanguage, true);
  // Load other languages during idle time to avoid blocking the main thread
  scheduleIdleWork(() => {
    loadOtherLanguages(defaultLanguage).catch((error) => {
      console.warn(
        'Failed to load some language resources during idle time:',
        error,
      );
    });
  });

  // change the language to the initial language
  if (labelI18nInstance.language !== initialLanguage) {
    await labelI18nInstance.changeLanguage(initialLanguage);
  }
};

const exists = (labelKey: unknown) => {
  try {
    // const lang = i18n.language || i18n.labelI18nInstance.language;
    const resources = labelI18nInstance?.store?.data?.['en']?.translation;
    if (resources) {
      return (
        resources && Object.prototype.hasOwnProperty.call(resources, labelKey)
      );
    }
    return 'NA';
  } catch (e: any) {
    return false;
  }
};

async function logUntranslated(
  labelKey: unknown,
  labelValue: unknown,
  stack?: string,
) {
  try {
    if (!labelKey || typeof labelKey !== 'string' || exists(labelKey)) {
      return;
    }
    if (localUntranslatedSet.has(labelKey)) {
      return;
    }
    localUntranslatedSet.add(labelKey);

    const best = await mapStackToBestOriginal(stack);

    let sourceLocation = 'unknown';
    if (best && best.source !== 'unknown') {
      const filePath = `${best.source}:${best.line}:${best.column}`;
      sourceLocation = `in ${filePath}`;
    }

    const formattedValue = `${labelValue} $$ ${sourceLocation}`;

    const latest = JSON.parse(
      localStorage.getItem('untranslatedLabels') || '{}',
    );
    latest[labelKey] = formattedValue;
    localStorage.setItem('untranslatedLabels', JSON.stringify(latest));
  } catch (e: any) {
    console.error('logUntranslated error', e);
  }
}

export const loadLanguageAndTranslationResourceBundle = async (
  language: unknown,
  isDefaultLanguage = false,
) => {
  if (labelI18nInstance.hasResourceBundle(language, 'translation')) {
    return;
  }
  const translations = await loadTranslations(language);
  if (translations) {
    labelI18nInstance.addResourceBundle(language, 'translation', translations);
    if (isDefaultLanguage) {
      labelI18nInstance.changeLanguage(language);
    }
  }
};

export const loadOtherLanguages = async (defaultLanguage: unknown) => {
  const languages = getAvailableLanguages();
  // Load languages one by one during idle periods to avoid blocking
  for (const language of languages) {
    if (language !== defaultLanguage) {
      // Schedule each language load during idle time
      await new Promise((resolve: unknown) => {
        scheduleIdleWork(async () => {
          try {
            await loadLanguageAndTranslationResourceBundle(language);
          } catch (error: any) {
            console.warn(`Failed to load language ${language}:`, error);
          }
          resolve();
        });
      });
    }
  }
};

export const ensureInitialized = async (
  agencyLanguage: unknown,
  shouldLogMissingTranslations = true,
) => {
  const enableMissingTranslationsLogs =
    shouldLogMissingTranslations && isMissingTranslationsLoggingEnabled();
  if (!i18nInitialized) {
    await initializeTranslations(agencyLanguage, enableMissingTranslationsLogs);
  }
};

export default labelI18nInstance;
